// C-S5A-1 «Подсказки на основе анкеты» — Stage 4 §6.
//
// Самостоятельно дёргает useWellnessInsights(), без props.
// Рендерится ТОЛЬКО внутри HomeEmpty.

import { useEffect, useRef, useState } from 'react';
import { Loader2, Star } from 'lucide-react';
import { useWellnessInsights } from '@/api/hooks/wellness';
import type {
  WellnessInsight,
  WellnessInsightsReady,
  WellnessInsightsResponse,
} from '@/api/types';

const POLL_TIMEOUT_MS = 60_000;

export type CardState = 'loading' | 'loaded' | 'refreshing' | 'unavailable';

type DeriveStateInput = {
  isLoading: boolean;
  isError: boolean;
  status: 'ready' | 'generating' | undefined;
  hasStash: boolean;
  isTimedOut: boolean;
};

export function deriveState(input: DeriveStateInput): CardState {
  const { isLoading, isError, status, hasStash, isTimedOut } = input;

  // Silent fallback на stash — нет реального refresh'а в процессе.
  if (isTimedOut && hasStash) return 'loaded';
  if (isError && hasStash) return 'loaded';

  // Без stash → unavailable.
  if (isTimedOut) return 'unavailable';
  if (isError) return 'unavailable';

  if (status === 'ready') return 'loaded';
  if (status === 'generating' && hasStash) return 'refreshing';
  if (status === 'generating' && !hasStash) return 'loading';

  if (isLoading) return 'loading';

  return 'loading';
}

export function renderInsights(
  state: CardState,
  data: WellnessInsightsResponse | undefined,
  stash: WellnessInsightsReady | null,
): WellnessInsight[] {
  if (state === 'loaded') {
    if (data?.status === 'ready') return data.insights;
    if (stash !== null) return stash.insights;
  }
  if (state === 'refreshing' && stash !== null) return stash.insights;
  return [];
}

export function pickDisclaimer(
  state: CardState,
  data: WellnessInsightsResponse | undefined,
  stash: WellnessInsightsReady | null,
): string {
  if (state === 'loaded') {
    if (data?.status === 'ready') return data.disclaimer;
    if (stash !== null) return stash.disclaimer;
  }
  if (state === 'refreshing' && stash !== null) return stash.disclaimer;
  return '';
}

export function WellnessInsightsCard() {
  const generatingStartedAt = useRef<number | null>(null);
  const [isTimedOut, setIsTimedOut] = useState(false);

  const query = useWellnessInsights({ enabled: !isTimedOut });

  const lastReadyRef = useRef<WellnessInsightsReady | null>(null);
  useEffect(() => {
    if (query.data?.status === 'ready') {
      lastReadyRef.current = query.data;
    }
  }, [query.data]);

  useEffect(() => {
    const status = query.data?.status;
    if (status === 'ready' || query.isError) {
      generatingStartedAt.current = null;
      setIsTimedOut(false);
      return;
    }
    if (status === 'generating') {
      if (generatingStartedAt.current === null) {
        generatingStartedAt.current = Date.now();
      }
      const elapsed = Date.now() - generatingStartedAt.current;
      const remaining = Math.max(0, POLL_TIMEOUT_MS - elapsed);
      if (remaining === 0) {
        setIsTimedOut(true);
        return;
      }
      const t = setTimeout(() => setIsTimedOut(true), remaining);
      return () => clearTimeout(t);
    }
  }, [query.data?.status, query.isError]);

  const state = deriveState({
    isLoading: query.isLoading,
    isError: query.isError,
    status: query.data?.status,
    hasStash: lastReadyRef.current !== null,
    isTimedOut,
  });

  return (
    <section
      className="rounded-surface border border-primary/20 bg-primary/5 p-5 transition-opacity duration-300"
      aria-live="polite"
      aria-busy={state === 'loading' || state === 'refreshing'}
    >
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-control bg-primary text-primary-foreground">
          <Star className="h-4 w-4 fill-current" aria-hidden="true" />
        </div>
        <h2 className="text-sm font-semibold text-primary-text">Подсказки на основе анкеты</h2>
      </div>

      {state === 'refreshing' && (
        <div
          className="mb-3 flex items-center gap-2 rounded-md bg-primary/10 px-3 py-2 text-hl-small text-primary-text"
          role="status"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          <span>Обновляем подсказки на основе новых ответов…</span>
        </div>
      )}

      {state === 'loading' && (
        <div className="space-y-2.5" aria-hidden="true">
          <div className="h-4 w-full animate-pulse rounded bg-primary/30" />
          <div className="h-4 w-[85%] animate-pulse rounded bg-primary/30" />
          <div className="h-4 w-[70%] animate-pulse rounded bg-primary/30" />
        </div>
      )}

      {(state === 'loaded' || state === 'refreshing') && (
        <>
          <ul role="list" className="mb-3 space-y-2.5">
            {renderInsights(state, query.data, lastReadyRef.current).map((insight) => (
              <li
                key={insight.id}
                className="flex items-start gap-2.5 text-hl-secondary leading-snug text-foreground"
              >
                <span className="mt-0.5 text-primary-text" aria-hidden="true">•</span>
                <span>{insight.text}</span>
              </li>
            ))}
          </ul>
          <p className="text-hl-small leading-snug text-muted-foreground">
            {pickDisclaimer(state, query.data, lastReadyRef.current)}
          </p>
        </>
      )}

      {state === 'unavailable' && (
        <div className="py-2 text-center text-sm text-muted-foreground">
          <p>Подсказки временно недоступны.</p>
          <p className="mt-1">Откройте экран позже — мы их добавим.</p>
        </div>
      )}
    </section>
  );
}
